import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Router} from "@angular/router";
import {IconProp} from "@fortawesome/fontawesome-svg-core";
import {StateConfigService} from "../../../../../services/state/state-config.service";
import {NgClass} from '@angular/common';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';

@Component({
    selector: 'app-sidebar-item',
    templateUrl: './sidebar-item.component.html',
    styleUrls: ['./sidebar-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [NgClass, FaIconComponent]
})
export class SidebarItemComponent implements OnInit, OnChanges, AfterViewInit {
  private router = inject(Router);
  private route = inject(ActivatedRoute);


  @Input() opened: boolean = true;
  @Input() collapsed: boolean = false;
  @Input() title: string = '';
  @Input() icon!: IconProp;
  @Input() ifc: boolean = false;
  @Input() chatBIC: boolean = false;
  @Input() disabled: boolean = false;
  @Input() beta: boolean = false;
  @Input() comingSoon: boolean = false;
  @Input() routerLinkName: string = '';
  @Input() selected: string = '';
  /**
   * Weitere URL-Fragmente, die diesen Eintrag aktiv markieren, ohne die an {@link selected}
   * hängenden Optik-Regeln zu berühren. Gebraucht, weil myCheck auf `my-checks` führt, Ausführung
   * und Builder aber unter `check-runs` liegen.
   */
  @Input() alsoSelectedFor: string[] = [];
  @ViewChild('sidebarIconHost', {read: ElementRef}) sidebarIconHost?: ElementRef<HTMLElement>;
  selectedElement: string = '';
  hovering: boolean = false;

  ngOnInit(): void {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        let end = event.url.split("/").pop();
        if(!isNaN(Number(end))){
          end = "dashboard";
        }

        end = end?.split("?")[0];

        const urlQuery = event.url.split("?")[1] ?? '';
        const hasDefinitionIdParam = urlQuery.includes('definitionId=');
        const selectedInQuery = urlQuery.includes(`${this.selected}`);
        const selectedInUrl = [this.selected, ...this.alsoSelectedFor]
          .some(fragment => !!fragment && event.url.includes(fragment));

        if (selectedInUrl) {
          // When a pinned check is active (definitionId query param present),
          // suppress the parent module item (e.g. 'check-runs') so only the pin
          // is highlighted. The pin itself still matches because its selected
          // value (definitionId) appears in the query string.
          if (hasDefinitionIdParam && !selectedInQuery) {
            this.selectedElement = end || '';
          } else {
            this.selectedElement = this.selected;
          }
        } else {
          this.selectedElement = end || '';
        }
      }
    });
  }

  ngAfterViewInit(): void {
    this.scheduleIconNormalization();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['icon'] && !changes['icon'].firstChange) {
      this.scheduleIconNormalization();
    }
  }

  onHover(): void {
    this.hovering = true;
  }

  onLeaveHover(): void {
    this.hovering = false;
  }

  private scheduleIconNormalization(attempt: number = 0): void {
    requestAnimationFrame(() => {
      const normalized = this.normalizeIconViewBox();
      if (!normalized && attempt < 8) {
        this.scheduleIconNormalization(attempt + 1);
      }
    });
  }

  /**
   * Centers the actual icon glyph inside a square viewBox.
   * This removes per-icon visual offset caused by uneven whitespace in source SVG paths.
   */
  private normalizeIconViewBox(): boolean {
    const host = this.sidebarIconHost?.nativeElement;
    if (!host) {
      return false;
    }

    const svg = host.querySelector('svg') as SVGSVGElement | null;
    if (!svg) {
      return false;
    }

    let bbox: DOMRect | SVGRect;
    try {
      // Prefer the rendered glyph bounds instead of the original viewBox.
      bbox = svg.getBBox();
    } catch {
      return false;
    }

    if (!bbox || bbox.width <= 0 || bbox.height <= 0) {
      return false;
    }

    const side = Math.max(bbox.width, bbox.height);
    const centerX = bbox.x + bbox.width / 2;
    const centerY = bbox.y + bbox.height / 2;
    const viewBoxX = centerX - side / 2;
    const viewBoxY = centerY - side / 2;

    svg.setAttribute('viewBox', `${viewBoxX} ${viewBoxY} ${side} ${side}`);
    svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    return true;
  }

  protected readonly StateConfigService = StateConfigService;
}
